import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { SelectNewWalletModal } from '@pages/Traders-Room/Balances/Wallets/modals/SelectNewWallet.modal'

import { WalletsExperience } from '@features/wallets'
import { useWalletRecentActivity } from '@features/wallets/hooks/useWalletRecentActivity'
import { useWalletsPageData } from '@features/wallets/hooks/useWalletsPageData'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { TableNoResults } from '@ui/v2/Table/Table'

import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { Loading } from '@shared/components/Loading/Loading'

import { useDepositTracker } from '@analytics/amplitude/deposit'

export const PaymentAgentWalletsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { formatMoney } = useFormatNumber()
  const { navigateToFunding } = useFundingNav()
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()
  const tracker = useDepositTracker()
  const addWalletModal = useModal(SelectNewWalletModal)

  const {
    wallets,
    walletCurrencies,
    selectedWallet,
    selectWallet,
    totalBalance,
    availableCurrencies,
    availableCurrencyIds,
    createWallet,
    isWalletsLoading,
    isTotalBalanceLoading,
    isTotalBalanceError,
    isAvailableCurrenciesLoading,
    isAvailableCurrenciesError,
    isCreatingWallet,
    isError,
  } = useWalletsPageData({ walletType: WalletTypeEnum.PAYMENT_AGENT })

  const recentActivity = useWalletRecentActivity({
    room: 'paymentAgent',
    walletType: WalletTypeEnum.PAYMENT_AGENT,
    walletId: selectedWallet?.id,
    context: 'paymentAgent',
    historyPath: CommonPaths.PaymentAgentTransactionHistory,
  })

  const isInitialLoading = isWalletsLoading || isAvailableCurrenciesLoading

  if (isInitialLoading) {
    return <Loading showLoadingIcon fullWidth isLoading />
  }

  if (isError || (!selectedWallet && wallets.length > 0)) {
    return <TableNoResults title={t('errors.Something went wrong')} />
  }

  const isCreateWalletRestricted = isRestricted(
    WalletRestrictions.CREATE_WALLET,
    account?.restrictions
  )
  const walletList = {
    totalBalance: {
      label: t('Wallet.Total'),
      information: t('Wallet.Total available balance description'),
      value: totalBalance ? formatMoney(totalBalance.balance, totalBalance.currency.id) : undefined,
      isLoading: isTotalBalanceLoading,
      errorMessage: isTotalBalanceError ? t('errors.Something went wrong') : undefined,
      subtitle: t('Wallet.Wallet'),
      currencies: walletCurrencies,
    },
    items: wallets.map((wallet) => ({
      id: wallet.id,
      currencyCode: wallet.currency.id,
      currencyName: wallet.currency.name,
      balance: formatMoney(wallet.balance, wallet.currency.id),
      isCurrent: wallet.id === selectedWallet?.id,
      onSelect: () => selectWallet(wallet.id),
    })),
    currentLabel: t('Current'),
    addCurrency:
      !isAvailableCurrenciesError && availableCurrencies.length > 0
        ? {
            label: t('Add Currency'),
            subtitle: t('Wallet.Wallet'),
            currencies: availableCurrencyIds,
            disabled: isCreateWalletRestricted,
            loading: isCreatingWallet,
            disabledMessage: isCreateWalletRestricted
              ? t('errors.Client is restricted to create a wallet')
              : undefined,
            onSelect: () =>
              addWalletModal.open({
                walletTypes: availableCurrencies,
                onSelect: createWallet,
              }),
          }
        : undefined,
  }

  if (!selectedWallet) {
    return (
      <WalletsExperience
        walletList={walletList}
        emptyState={{
          title: t('Wallet.No wallets title'),
          description: t('Wallet.No wallets description'),
        }}
      />
    )
  }

  const isDepositDisabled = isRestricted(WalletRestrictions.DEPOSIT, selectedWallet.restrictions)
  const isWithdrawDisabled = isRestricted(WalletRestrictions.WITHDRAW, selectedWallet.restrictions)
  const isTransferDisabled = isRestricted(
    WalletRestrictions.TRANSFER_FROM,
    selectedWallet.restrictions
  )

  return (
    <WalletsExperience
      currentWallet={{
        balanceLabel: t('Wallet.Available Balance'),
        balance: formatMoney(selectedWallet.balance, selectedWallet.currency.id),
        currencyCode: selectedWallet.currency.id,
        walletNumber: selectedWallet.name,
        information: t('Wallet.Payment Agent wallet description'),
        copyWalletNumberLabel: t('Copy wallet number'),
        breakdown: [
          {
            id: 'reserved',
            label: t('Wallet.Reserved'),
            value: formatMoney(selectedWallet.reserved, selectedWallet.currency.id),
          },
        ],
        actions: {
          items: [
            {
              kind: 'transfer',
              label: t('Transfer'),
              disabled: isTransferDisabled,
              onClick: () =>
                navigate(`${CommonPaths.PaymentAgentWallets}/${selectedWallet.id}/transfer`),
            },
            {
              kind: 'deposit',
              label: t('Deposit'),
              disabled: isDepositDisabled,
              onClick: () => {
                tracker.start({
                  entry_point: 'payment_agent',
                  preset_wallet_id: selectedWallet.id,
                })
                navigateToFunding(`${CommonPaths.PaymentAgentDeposit}/wallet`, {
                  state: { selectedWalletId: selectedWallet.id },
                })
              },
            },
            {
              kind: 'withdraw',
              label: t('Withdraw'),
              disabled: isWithdrawDisabled,
              loading: walletWithdrawLoadingId === selectedWallet.id,
              onClick: () => handleWithdrawalNavigation({ walletId: selectedWallet.id }),
            },
          ],
          primaryAction: 'transfer',
          mobileOrder: ['deposit', 'transfer', 'withdraw'],
          menuLabel: t('Wallet actions'),
          actionUnavailableMessage: t('Wallet.Action unavailable'),
          walletUnavailableMessage: t('Wallet.Transactions unavailable'),
        },
      }}
      walletList={walletList}
      recentActivity={recentActivity}
    />
  )
}
